import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { catchError, debounceTime, distinctUntilChanged, map, of, startWith, switchMap, tap } from 'rxjs';
import { TSP } from '../../../core/models';
import { CompetenceService } from '../../../core/services/competence/competence.service';
import { COMPETENCE_KIND_LABELS } from '../competences.component';

export interface CompetenceMergeDialogData {
  competence: TSP.Internal.Dictionary.Competence.AdminItem;
}

export interface CompetenceMergeDialogResult {
  targetId: number;
}

@Component({
  selector: 'app-competence-merge-dialog',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButton,
    MatDialogActions,
    MatDialogContent,
    MatDialogTitle,
    MatError,
    MatFormField,
    MatIcon,
    MatInput,
    MatLabel,
    MatOption,
    MatSelect,
  ],
  templateUrl: './competence-merge-dialog.html',
  styleUrl: './competence-merge-dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompetenceMergeDialog {
  private readonly dialogRef = inject(MatDialogRef<CompetenceMergeDialog>);
  private readonly competenceService = inject(CompetenceService);
  private readonly destroyRef = inject(DestroyRef);
  public readonly data = inject<CompetenceMergeDialogData>(MAT_DIALOG_DATA);

  public searchControl = new FormControl<string>('');
  public targetControl = new FormControl<number | null>(null, [Validators.required]);

  public readonly isLoading = signal(true);

  private static readonly PAGE_SIZE = 50;

  // Targets are ACTIVE rows only: merging into PENDING makes an unvetted row
  // canonical, and merging into MERGED is rejected by the dictionary anyway.
  public readonly filteredCompetences$ = this.searchControl.valueChanges.pipe(
    startWith(''),
    distinctUntilChanged(),
    tap(() => this.isLoading.set(true)),
    debounceTime(300),
    switchMap((search) =>
      this.competenceService
        .getCompetencesList({
          search: search || undefined,
          status: [TSP.Internal.Dictionary.Competence.Status.ACTIVE],
          size: CompetenceMergeDialog.PAGE_SIZE,
        })
        .pipe(
          map((response) => response.items.filter((competence) => competence.id !== this.data.competence.id)),
          catchError(() => of<TSP.Internal.Dictionary.Competence.AdminItem[]>([])),
        ),
    ),
    tap(() => this.isLoading.set(false)),
    takeUntilDestroyed(this.destroyRef),
  );

  public competenceLabel(competence: TSP.Internal.Dictionary.Competence.AdminItem): string {
    return `${competence.name} (${COMPETENCE_KIND_LABELS[competence.kind]})`;
  }

  public onCancel(): void {
    this.dialogRef.close(null);
  }

  public onConfirm(): void {
    if (this.targetControl.invalid || !this.targetControl.value) {
      this.targetControl.markAsTouched();
      return;
    }
    this.dialogRef.close({ targetId: this.targetControl.value } satisfies CompetenceMergeDialogResult);
  }
}
